/* eslint-disable react-hooks/exhaustive-deps */
import { toFeatureState } from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { TheWestSection } from '@news-mono/common'
import {
    AppState,
    AuthenticationState,
    isComponentEnabled,
    isPuzzlePage,
    RequiredAccess,
    useFeature,
    useToggleState,
} from '@news-mono/web-common'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { FeatureToggle, useSection } from '../..'
import {
    StyledAd,
    StyledHeaderContainer,
    StyledNavigation,
    StyledTheWestLogo,
    StyledTWLogoWrapHeading,
} from '../../content/TheWestStickySiteHeader/TheWestStickySiteHeader.styled'
import { addListener, removeListener } from '../../__helpers/global-dom-events'
import { DimensionProps, withDimensions } from '../../__helpers/with-dimensions'

export interface TheWestStickySiteHeaderProps {
    headerAd?: (rendered?: () => void) => JSX.Element | undefined

    header: JSX.Element
    navigation: JSX.Element
    disableSticky?: boolean
    pageProperties?: {
        requiredAccess: RequiredAccess
        publicationKind: 'article' | 'redirect' | 'event'
        layout?: 'default' | 'feature'
    }
}

type InternalProps = TheWestStickySiteHeaderProps & DimensionProps

export const InternalTheWestStickySiteHeader: React.FC<InternalProps> = (
    props,
) => {
    const section = useSection<TheWestSection>()
    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )
    const location = useLocation()
    const toggleState = useToggleState()

    const [currentScrollPos, changeScrollPos] = useState(0)
    const [prevScrollPos, setPrevScrollPos] = useState(0)

    const [isHidden, changeHidden] = useState(false)
    const [isTop, changeTop] = useState(true)
    const [scrollUp, changeScrollUp] = useState(false)

    const [hideBreachAds, setHideBreachAds] = useState(false)
    /**
     * Checks viewport scroll level.
     * If user has scrolled over scrollTarget in px it sets the component to hidden and it scrolls off the page.
     * Upon scrolling up, it checks if the component is hidden and brings it back into view
     */

    const handleScroll = () => {
        changeScrollPos(window.pageYOffset)
    }

    useEffect(() => {
        const scrollTarget = 500
        const scrollTreshold = 150
        const shouldHide = currentScrollPos >= scrollTarget
        const userScrollUp = currentScrollPos <= prevScrollPos
        const userScrollDistance = Math.abs(currentScrollPos - prevScrollPos)
        /**
         * Dont want the component appearing after small scroll up, so calculate the distance
         * user has scrolled and see if it has met a certain distance threshold
         */
        const scrollUpThresholdMet =
            userScrollDistance >= scrollTreshold ? true : false

        if (
            (currentScrollPos === 0 && isHidden) ||
            (!props.disableSticky &&
                userScrollUp &&
                scrollUpThresholdMet &&
                isHidden)
        ) {
            changeHidden(false)
            changeTop(false)
            changeScrollUp(true)
        } else if (shouldHide && !userScrollUp && !isHidden) {
            changeHidden(true)
            changeTop(false)
            changeScrollUp(false)
        }
        setPrevScrollPos(currentScrollPos)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScrollPos])

    useEffect(() => {
        addListener('scroll', handleScroll)
        return () => {
            removeListener('scroll', handleScroll)
        }
    }, [])

    /** Breachwall Checks */
    /** Disable/Hide Ad wrapper for breachwall pages */
    const disableAdsToggle = useFeature('breach-disable-ads')
    const pageProps = props.pageProperties
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    useEffect(() => {
        const isEntitled = authentication.isEntitled
        const disableBreachAds =
            disableAdsToggle &&
            pageProps?.publicationKind === 'article' &&
            pageProps?.requiredAccess.level === 'subscriber' &&
            !isEntitled

        disableBreachAds && setHideBreachAds(true)
        !disableBreachAds && setHideBreachAds(false)
    }, [pageProps])

    const disableHeaderAd =
        renditionType === 'app' && isPuzzlePage({ location })

    const isGenWestHeaderAdEnabled = isComponentEnabled(
        toFeatureState(toggleState),
        {
            feature: 'genwest-ad-slots',
        },
    )

    return (
        <React.Fragment>
            <FeatureToggle
                feature="the-west-bar-regional"
                on={() =>
                    section !== 'default' && section !== 'genwest' ? (
                        <StyledTWLogoWrapHeading>
                            <a
                                href={`https://www.thewest.com.au/?utm_medium=referral&utm_campaign=${section}&utm_content=header#logo`}
                                target="_blank"
                            >
                                <StyledTheWestLogo />
                            </a>
                        </StyledTWLogoWrapHeading>
                    ) : (
                        <></>
                    )
                }
            />
            {section !== 'genwest'
                ? !disableHeaderAd && renderHeaderAd(hideBreachAds)
                : isGenWestHeaderAdEnabled &&
                  !disableHeaderAd &&
                  renderHeaderAd(hideBreachAds)}
            {section !== 'genwest' && (
                <StyledNavigation
                    className="header-navigation"
                    scrollUp={scrollUp}
                    isHidden={isHidden}
                    isTop={isTop}
                    height={props.height}
                >
                    {props.navigation}
                </StyledNavigation>
            )}

            <StyledHeaderContainer className="masthead">
                {props.header}
            </StyledHeaderContainer>
        </React.Fragment>
    )

    function renderHeaderAd(hideBreachAds: boolean) {
        if (hideBreachAds) {
            return (
                props.headerAd && (
                    <StyledAd
                        className="disabled-ad"
                        ref={props.innerRef}
                        isHidden={isHidden}
                        scrollUp={scrollUp}
                        isTop={isTop}
                        height={props.height}
                    ></StyledAd>
                )
            )
        }
        return (
            props.headerAd && (
                <StyledAd
                    className="headerAdvertisement"
                    ref={props.innerRef}
                    isHidden={isHidden}
                    scrollUp={scrollUp}
                    isTop={isTop}
                    height={props.height}
                >
                    {props.headerAd(() => props.triggerMeasure())}
                </StyledAd>
            )
        )
    }
}

export const TheWestStickySiteHeader =
    withDimensions<TheWestStickySiteHeaderProps>(
        InternalTheWestStickySiteHeader,
    )

TheWestStickySiteHeader.displayName = 'TheWestStickySiteHeader'
